import {
  LoadingSpinner,
  NoFlyersAvailableSectionEmptyState,
  PersistentTooltip,
  ViewMoreCard,
} from '@/components';
import AuthenticationContext from '@/context/AuthenticationContext';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import { BodyLargeEmphasized } from '@/design-system';
import { AllFlyerFilters } from '@/graphql/types';
import { useFlyerboardPagination, useGoogleTranslator } from '@/hooks';
import colors from '@/theme/colors';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { CommunityDisclaimerModal } from '../CommunityDisclaimerModal';
import { FlyerList } from './FlyerList';
import styles from './FlyerboardPagination.styles';
import { ViewMode } from '@/config/constants';

type Props = {
  trackId: string;
  flyerboardFilters: AllFlyerFilters;
  flyerType: string;
  audienceId?: string;
  hasMyCommSchoolFlyers: boolean;
  setHasOtherSchoolFlyers: (hasOtherSchoolFlyers: boolean) => void;
};

const EXTENDED_COMMUNITY_TOOLTIP = `Extended community consists of more opportunities suitable for my child’s grade/age that are distributed through other schools in my local area.`;

export const OtherSchoolFlyers: React.FC<Props> = ({
  trackId,
  flyerboardFilters,
  flyerType,
  audienceId,
  hasMyCommSchoolFlyers,
  setHasOtherSchoolFlyers,
}) => {
  const tooltipTranslated = useGoogleTranslator(EXTENDED_COMMUNITY_TOOLTIP);
  const { community, viewWholeCommunityFlyers } = useContext(UserPreferencesContext);

  const { isAuthenticated } = useContext(AuthenticationContext);
  const [showCommDisclaimerModal, setShowCommDisclaimerModal] = useState<boolean>(false);

  const { lat, lng } = community?.location;

  const otherSchoolFilters: AllFlyerFilters = {
    ...flyerboardFilters,
    allDistrictSchools: true,
    communityLocation: isAuthenticated && community?.location ? { lat, lng } : undefined,
    communityRadius: isAuthenticated && community.distance ? community.distance : undefined,
  };

  const { flyers, dataNotFound, loading, total, fetchMoreFlyers } = useFlyerboardPagination({
    flyerboardFilters: otherSchoolFilters,
    flyerType,
    shouldUpdateFacets: false,
    audienceId,
  });

  useEffect(() => {
    if (total === 0) {
      setHasOtherSchoolFlyers(false);
    }
  }, [total]);

  const showHeader = !loading && (!dataNotFound || hasMyCommSchoolFlyers);

  const onSeeMoreFlyersClick = () => {
    setShowCommDisclaimerModal(true);
  };

  const onConfirmCommDisclaimer = () => {
    onCancelCommDisclaimer();
  };

  const onCancelCommDisclaimer = () => {
    setShowCommDisclaimerModal(false);
  };

  const EmptyState = () => {
    if (loading || flyers.length > 0 || !hasMyCommSchoolFlyers) return null;

    return <NoFlyersAvailableSectionEmptyState />;
  };

  const CommunityToolTip = () => (
    <span className={styles.tooltipButton}>
      <PersistentTooltip title={tooltipTranslated}>
        <IconButton aria-label="About Extended Community">
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </PersistentTooltip>
    </span>
  );

  const showWholeCommBanner = viewWholeCommunityFlyers === null && flyers.length > 0;

  if (showWholeCommBanner) {
    return (
      <>
        <ViewMoreCard
          title="EXTENDED COMMUNITY"
          description="View community opportunities fit for my child from other schools in my area."
          buttonLabel="View Extended Community"
          onClick={onSeeMoreFlyersClick}
        />
        <CommunityDisclaimerModal
          isOpen={showCommDisclaimerModal}
          onConfirm={onConfirmCommDisclaimer}
          onCancel={onCancelCommDisclaimer}
        />
      </>
    );
  }

  if (viewWholeCommunityFlyers === null) return null;

  return (
    <div className={styles.container}>
      <div className={styles.section}>
        {loading && flyers.length === 0 && <LoadingSpinner />}
        {showHeader && (
          <>
            <BodyLargeEmphasized color={colors.slate}>Extended Community</BodyLargeEmphasized>
            <CommunityToolTip />
          </>
        )}
        <EmptyState />
        <FlyerList
          trackId={trackId}
          flyers={flyers}
          nextPage={fetchMoreFlyers}
          total={total}
          audienceId={audienceId}
          trackingEventFrom="my_whole_community"
        />
      </div>
    </div>
  );
};
